import React from "react";
import { Banner, Button, FormField, Input, Select, StatusPill } from "@/ds";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type { PlatformMailboxSecret, PlatformMailboxView, PlatformMailDomainKind, PlatformMailDomains } from "@/api";
import type { PushToast } from "@/notifications/toast-queue";
import { useLang, useT } from "../i18n";
import {
  actionsFor,
  buildPlatformMailboxPayload,
  categoryTone,
  emptyPlatformMailboxForm,
  platformMailboxFormErrors,
  sortPlatformMailboxes,
  type PlatformMailboxForm,
} from "./screen-platform-mailboxes.logic";
import styles from "./screen-platform-mailboxes.module.css";

/**
 * The platform's own mailboxes on its mail domain (#1214), for the owner plane.
 *
 * Built around two facts. The list is the mail server's, not ours — everything on the domain
 * appears, classified, and the console acts only on what it is recorded as owning. And a
 * password exists in readable form exactly once, in the response that made it: the panel at
 * the top shows it, offers to copy it, says it will not be shown again, and forgets it when the
 * owner says they are done. The same panel serves creating and resetting; there is no "show
 * password" anywhere else because there is nothing to show.
 *
 * Destructive steps are two clicks in place (the pattern of the Businesses page), with the
 * cost stated before the second click.
 *
 * Since #1899 the list may span two domains, and a row is keyed by its full address: the same
 * local part can exist once on each. A work mailbox is never opened from here, and the panel that
 * shows its password also says where its person signs in.
 */

/** How long "Kopiert" stays on the button before it offers to copy again. */
const COPIED_FEEDBACK_MS = 2000;

type Pending = { kind: "reset" | "delete"; emailAddress: string } | null;

export function PlatformMailboxesScreen({ pushToast }: { pushToast: PushToast }) {
  const t = useT();
  const { lang } = useLang();
  // #1536: the mailboxes from the last visit stay on screen while they refresh.
  const [rows, setRows] = React.useState<PlatformMailboxView[] | null>(() => {
    const remembered = cachedRead("listPlatformMailboxes");
    return remembered ? sortPlatformMailboxes(remembered) : null;
  });
  const [error, setError] = React.useState<string | null>(null);
  /** Null until known, and when unknown: then only the platform domain is offered. */
  const [domains, setDomains] = React.useState<PlatformMailDomains | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [form, setForm] = React.useState<PlatformMailboxForm>(emptyPlatformMailboxForm);
  const [formError, setFormError] = React.useState<string | null>(null);
  /** Held in memory only, and only until the owner dismisses it. The server cannot reproduce it. */
  const [secret, setSecret] = React.useState<PlatformMailboxSecret | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [pending, setPending] = React.useState<Pending>(null);
  const copyRowRef = React.useRef<HTMLDivElement>(null);

  const formatDate = React.useCallback(
    (iso: string) => new Date(iso).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB"),
    [lang],
  );

  const load = React.useCallback(() => {
    setError(null);
    cockpit.listPlatformMailboxDomains().then(setDomains, () => setDomains(null));
    cockpit.listPlatformMailboxes().then(
      (list) => setRows(sortPlatformMailboxes(list)),
      (e: any) => {
        // Fail closed in the UI too: an unreachable mail server is an error, never an empty list.
        setRows([]);
        setError(e?.message || t("common.error"));
      },
    );
  }, [t]);

  React.useEffect(() => {
    load();
  }, [load]);

  // The moment a password exists, the next thing to do is copy it, so that is where focus goes.
  React.useEffect(() => {
    if (!secret) return;
    copyRowRef.current?.querySelector("button")?.focus();
  }, [secret]);

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const fail = (e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message });

  const showSecret = (fresh: PlatformMailboxSecret) => {
    setCopied(false);
    setSecret(fresh);
  };

  const create = () => {
    const errors = platformMailboxFormErrors(form);
    if (errors.length) {
      setFormError(errors.map((key) => t(key)).join(" · "));
      return;
    }
    setBusy(true);
    setFormError(null);
    cockpit
      .createPlatformMailbox(buildPlatformMailboxPayload(form))
      .then((fresh) => {
        showSecret(fresh);
        setForm(emptyPlatformMailboxForm());
        setCreateOpen(false);
        pushToast({ tone: "success", title: t("platformMailbox.created", { address: fresh.emailAddress }) });
        load();
      })
      .catch((e: any) => setFormError(e?.message || t("common.error")))
      .finally(() => setBusy(false));
  };

  /**
   * Opens the mailbox in the webmail (#1235, #1265).
   *
   * The tab is opened on the click and pointed afterwards: a browser blocks a window opened from
   * an async callback, and the server has to rotate the credential and write the audit row before
   * anyone is sent anywhere.
   *
   * The ticket is POSTed rather than appended to the URL, which is why this builds a form instead
   * of assigning `location.href`: a query string is written to the webmail's access log, and a
   * spent ticket sitting in a log is a secret kept for no reason. The form is built with DOM calls
   * rather than written as markup so that no inline script is involved and the console's own
   * content policy has nothing to refuse.
   *
   * Deliberately without `noopener`, which makes `window.open` return null and leaves nothing to
   * write into; the handle is severed after the form is submitted instead.
   */
  const openInWebmail = (row: PlatformMailboxView) => {
    const tab = window.open("", "_blank");
    setBusy(true);
    cockpit
      .openPlatformMailboxInWebmail(row.localPart, row.domain)
      .then((session) => {
        if (!tab) {
          pushToast({ tone: "warning", title: t("platformMailbox.openFailed") });
          return;
        }
        const doc = tab.document;
        const form = doc.createElement("form");
        form.method = "POST";
        form.action = session.url;
        const ticket = doc.createElement("input");
        ticket.type = "hidden";
        ticket.name = "_ticket";
        ticket.value = session.ticket;
        form.appendChild(ticket);
        doc.body.appendChild(form);
        form.submit();
        tab.opener = null;
      })
      .catch((e: any) => {
        tab?.close();
        fail(e);
      })
      .finally(() => setBusy(false));
  };

  const adopt = (row: PlatformMailboxView) => {
    setBusy(true);
    cockpit
      .adoptPlatformMailbox(row.localPart, row.domain)
      .then(() => {
        pushToast({ tone: "success", title: t("platformMailbox.adopted", { address: row.emailAddress }) });
        load();
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const reset = (row: PlatformMailboxView) => {
    setBusy(true);
    cockpit
      .resetPlatformMailboxPassword(row.localPart, row.domain)
      .then((fresh) => {
        setPending(null);
        showSecret(fresh);
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const remove = (row: PlatformMailboxView) => {
    setBusy(true);
    cockpit
      .deletePlatformMailbox(row.localPart, row.domain)
      .then(() => {
        setPending(null);
        if (secret?.emailAddress === row.emailAddress) setSecret(null);
        pushToast({ tone: "success", title: t("platformMailbox.deleted", { address: row.emailAddress }) });
        load();
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };

  /** A new form picks the work domain where there is one; one already being filled keeps its choice. */
  const toggleCreate = () => {
    setFormError(null);
    if (!createOpen) {
      setForm((current) =>
        current.localPart || current.description ? current : emptyPlatformMailboxForm(domains?.work ? "work" : "platform"),
      );
    }
    setCreateOpen((open) => !open);
  };

  const domainLabel = (kind: PlatformMailDomainKind) => `@${(kind === "work" ? domains?.work : domains?.platform) ?? ""}`;

  const selectWhole = (e: React.SyntheticEvent<HTMLInputElement>) => {
    e.currentTarget.select?.();
  };

  const copy = async () => {
    if (!secret) return;
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(secret.password);
      setCopied(true);
      pushToast({ tone: "success", title: t("platformMailbox.copied") });
    } catch {
      // Selecting the text is the honest fallback: the person can still copy with the keyboard,
      // and the toast tells them so instead of pretending the copy happened.
      copyRowRef.current?.querySelector("input")?.select();
      pushToast({ tone: "warning", title: t("platformMailbox.copyFailed") });
    }
  };

  const renderActions = (row: PlatformMailboxView) => {
    const actions = actionsFor(row.category, row.domain);
    if (actions.length === 0) {
      return <span className="subtle">{t(`platformMailbox.category.hint.${row.category}`)}</span>;
    }
    if (pending && pending.emailAddress === row.emailAddress) {
      const isDelete = pending.kind === "delete";
      return (
        <div className={styles.confirm} data-testid="platform-mailbox-confirm">
          <span className={styles.confirmText}>
            {isDelete
              ? t("platformMailbox.deleteConfirm", { address: row.emailAddress })
              : t("platformMailbox.resetConfirm")}
          </span>
          <div className={styles.confirmActions}>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setPending(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant={isDelete ? "destructive" : "primary"}
              size="sm"
              disabled={busy}
              onClick={() => (isDelete ? remove(row) : reset(row))}
              data-testid={isDelete ? "platform-mailbox-delete-now" : "platform-mailbox-reset-now"}
            >
              {isDelete ? t("platformMailbox.deleteNow") : t("platformMailbox.reset")}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.rowActions}>
        {actions.includes("open") && (
          <Button variant="primary" size="sm" disabled={busy} onClick={() => openInWebmail(row)} data-testid="platform-mailbox-open">
            {t("platformMailbox.open")}
          </Button>
        )}
        {actions.includes("adopt") && (
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => adopt(row)} data-testid="platform-mailbox-adopt">
            {t("platformMailbox.adopt")}
          </Button>
        )}
        {actions.includes("reset") && (
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => setPending({ kind: "reset", emailAddress: row.emailAddress })} data-testid="platform-mailbox-reset">
            {t("platformMailbox.reset")}
          </Button>
        )}
        {actions.includes("delete") && (
          <Button variant="danger-quiet" size="sm" disabled={busy} onClick={() => setPending({ kind: "delete", emailAddress: row.emailAddress })} data-testid="platform-mailbox-delete">
            {t("platformMailbox.delete")}
          </Button>
        )}
      </div>
    );
  };

  return (
    // #1431: the Settings → Service mailboxes section (Platform · owner), under Settings' heading.
    <div data-testid="platform-mailboxes">
      <div className="page-head page-head--section">
        <div>
          <p className="management-copy">{t("platformMailbox.sub")}</p>
        </div>
        <div className="spacer" />
        <div className="management-actions">
          <Button variant="primary" disabled={busy} onClick={toggleCreate} data-testid="platform-mailbox-create-toggle">
            {t("platformMailbox.create")}
          </Button>
        </div>
      </div>

      <Banner tone="info" title={t("platformMailbox.webmailHint")} />
      {domains?.work && <Banner tone="info" title={t("platformMailbox.workHint", { domain: domains.work })} />}

      {secret && (
        <section className={styles.secret} aria-labelledby="platform-mailbox-secret-title" data-testid="platform-mailbox-secret">
          <h2 id="platform-mailbox-secret-title" className={styles.sectionTitle}>
            {t("platformMailbox.secret.title", { address: secret.emailAddress })}
          </h2>
          <div className={styles.copyRow} ref={copyRowRef}>
            <Input
              readOnly
              value={secret.password}
              aria-label={t("platformMailbox.secret.password")}
              onFocus={selectWhole}
              onClick={selectWhole}
              data-testid="platform-mailbox-password"
            />
            <div className={styles.copyAction}>
              <Button variant="primary" size="sm" onClick={copy} data-testid="platform-mailbox-copy">
                {copied ? t("platformMailbox.copiedShort") : t("platformMailbox.copy")}
              </Button>
            </div>
          </div>
          <p className={styles.meta}>
            {t("platformMailbox.secret.username")} {t("platformMailbox.secret.once")}
          </p>
          {secret.domain === "work" && domains?.webmail && domains.mailHost && (
            <p className={styles.meta} data-testid="platform-mailbox-client-settings">
              {t("platformMailbox.secret.workClient", { webmail: domains.webmail, host: domains.mailHost })}
            </p>
          )}
          <div>
            <Button variant="secondary" size="sm" onClick={() => setSecret(null)} data-testid="platform-mailbox-secret-done">
              {t("platformMailbox.dismissSecret")}
            </Button>
          </div>
        </section>
      )}

      {createOpen && (
        <section className={styles.form} aria-labelledby="platform-mailbox-create-title" data-testid="platform-mailbox-create">
          <h2 id="platform-mailbox-create-title" className={styles.sectionTitle}>{t("platformMailbox.createTitle")}</h2>
          <div className={domains?.work ? `${styles.fields} ${styles.fieldsWithDomain}` : styles.fields}>
            {domains?.work && (
              <FormField label={t("platformMailbox.domain")}>
                <Select
                  value={form.domain}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setForm({ ...form, domain: e.target.value === "work" ? "work" : "platform" })}
                  options={[
                    { value: "work", label: domainLabel("work") },
                    { value: "platform", label: domainLabel("platform") },
                  ]}
                  data-testid="platform-mailbox-domain"
                />
              </FormField>
            )}
            <FormField label={t("platformMailbox.localPart")}>
              <Input
                value={form.localPart}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, localPart: e.target.value })}
                placeholder={form.domain === "work" ? "info" : "socialmedia"}
                data-testid="platform-mailbox-local-part"
              />
            </FormField>
            <FormField label={t("platformMailbox.description")} hint={t("platformMailbox.descriptionHint")}>
              <Input
                value={form.description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, description: e.target.value })}
                data-testid="platform-mailbox-description"
              />
            </FormField>
          </div>
          {formError && <Banner tone="danger" title={t("common.error")}>{formError}</Banner>}
          <div className={styles.formActions}>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => { setCreateOpen(false); setFormError(null); }}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" size="sm" disabled={busy} onClick={create} data-testid="platform-mailbox-submit">
              {t("platformMailbox.create")}
            </Button>
          </div>
        </section>
      )}

      {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
      {rows === null && !error && <p className="subtle" aria-busy="true">{t("common.loading")}</p>}
      {rows && rows.length === 0 && !error && <p className="subtle">{t("platformMailbox.none")}</p>}

      {rows && rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table} data-testid="platform-mailbox-table">
            <thead>
              <tr>
                <th scope="col">{t("platformMailbox.col.address")}</th>
                <th scope="col">{t("platformMailbox.col.category")}</th>
                <th scope="col">{t("platformMailbox.col.description")}</th>
                <th scope="col">{t("platformMailbox.col.created")}</th>
                <th scope="col">{t("platformMailbox.col.action")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.emailAddress} data-testid="platform-mailbox-row" data-category={row.category} data-domain={row.domain}>
                  <td data-label={t("platformMailbox.col.address")} className={styles.address}>{row.emailAddress}</td>
                  <td data-label={t("platformMailbox.col.category")}>
                    <StatusPill tone={categoryTone(row.category)} label={t(`platformMailbox.category.${row.category}`)} />
                  </td>
                  <td data-label={t("platformMailbox.col.description")}>
                    {row.description ? row.description : <span className="subtle">—</span>}
                  </td>
                  <td data-label={t("platformMailbox.col.created")} className="neo-num">
                    {row.createdAt ? formatDate(row.createdAt) : <span className="subtle">—</span>}
                  </td>
                  <td data-label={t("platformMailbox.col.action")} className={styles.actionCell}>
                    {renderActions(row)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
